import { getToolConfig, humanizeToolId } from '../../lib/tool-configs';
import { getRemoteToolConfig } from '../../lib/tool-configs/remote';
function object(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function brief(value) {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, 240) : '';
}
function sourceUrl(value) {
    if (typeof value !== 'string') return undefined;
    try {
        const url = new URL(value);
        return [
            'https:',
            'http:'
        ].includes(url.protocol) && !url.username && !url.password ? url.href : undefined;
    } catch  {
        return undefined;
    }
}
/** Summaries use returned evidence only; an unknown schema is never declared empty or verified. */ export function describeToolStep(part, active) {
    const name = part.toolName || part.type.replace(/^tool-?/, '');
    const config = getToolConfig(name);
    const displayName = config?.displayName || getRemoteToolConfig(name)?.displayName || humanizeToolId(name);
    const input = object(part.input);
    const target = brief(input.query || input.search_query || input.laborCategory || input.vendorName || input.name || input.filename || input.url);
    const purpose = brief(part.purpose) || (target ? `${displayName}: ${target}` : config?.loadingText || `Use ${displayName.toLowerCase()} for this request.`);
    const out = object(part.output);
    const data = object(out.data);
    const caveats = Array.isArray(out.warnings) ? out.warnings.map(brief).filter(Boolean) : [];
    const step = {
        name: displayName,
        purpose,
        status: 'Complete',
        result: 'Result available. Open details to inspect it.',
        caveats,
        sourceUrl: sourceUrl(data.finalUrl || data.url)
    };
    if (part.output === undefined || part.output === null) {
        step.status = active ? part.executionState === 'queued' ? 'Queued' : 'Working' : 'Not completed';
        step.result = active ? part.executionState === 'queued' ? 'Waiting for the previous step.' : 'Waiting for the tool to return.' : 'No result was saved for this step.';
        return step;
    }
    if (out.error || out.success === false || typeof out.exitCode === 'number' && out.exitCode !== 0) {
        step.status = 'Failed';
        step.result = brief(out.error) || brief(out.message) || brief(object(out.error).message) || 'The tool could not complete this step. See details.';
        return step;
    }
    if (out._documentWrite && out.approvalState === 'accepted') {
        step.result = 'Approved changes were applied to the document.';
        return step;
    }
    if (out._documentWrite && out.approvalState === 'rejected') {
        step.result = 'Proposed changes were rejected; the document was not changed.';
        return step;
    }
    if (out._requiresApproval || out._documentWrite || out._recordWrite) {
        step.status = 'Needs approval';
        step.result = 'Changes proposed. Review the card below to approve or reject them.';
        return step;
    }
    if (out.truncated === true || out.hasMore === true || out.more_chunks_available === true || data.truncated === true || caveats.length > 0) {
        step.status = 'Partial';
    }
    if (name === 'gsa-pricing' && Array.isArray(out.laborRates)) {
        const count = out.laborRates.length;
        step.result = count ? `Returned ${count} ceiling-rate rows from CALC+. These are schedule ceilings, not a quote.` : 'No matching rate rows returned for this search.';
        if (!count && step.status !== 'Partial') step.status = 'No matches';
    } else if (name === 'gsa-contractor-lookup' && Array.isArray(out.vendors)) {
        step.result = `Returned ${out.vendors.length} vendor candidates. Contract and rate alignment still needs checking.`;
        if (!out.vendors.length && step.status !== 'Partial') step.status = 'No matches';
        else if (step.status === 'Complete') step.status = 'Needs verification';
    } else if (Array.isArray(out.documents)) {
        step.result = out.documents.length ? `Found ${out.documents.length} document candidates. Their contents have not been read by this step.` : 'No documents matched this search.';
        if (!out.documents.length && step.status !== 'Partial') step.status = 'No matches';
    } else if (Array.isArray(out.chunks)) {
        step.result = `Read ${out.chunks.length} document sections${out.more_chunks_available ? '; more sections are available' : ''}.`;
    } else if (typeof out.text === 'string' && out.documentId) {
        step.result = `Read ${out.text.length.toLocaleString()} characters${out.hasMore ? ' from part of the document' : ' from the document'}.`;
    } else if (name === 'research-fetch-url' && typeof data.text === 'string') {
        step.result = `Retrieved ${data.text.length.toLocaleString()} characters of readable source text${data.truncated ? '; more text is available' : ''}.`;
    } else if (Array.isArray(data.sources)) {
        step.result = `Found ${data.sources.length} source links. Search results still need verification against originals.`;
        if (step.status === 'Complete') step.status = data.sources.length ? 'Needs verification' : 'No matches';
    } else if (name === 'create-office-document' && out.success === true && out.documentId) {
        step.result = `Created ${brief(out.title || out.name) || 'an Office document'}. Open the document card below to review it.`;
    } else if (Array.isArray(out.results) && /search/.test(name)) {
        step.result = `Returned ${out.results.length} matches for this search.`;
        if (!out.results.length && step.status !== 'Partial') step.status = 'No matches';
    } else if (name === 'execute-python' && out.success === true) {
        step.result = brief(out.stdout) ? `Code ran. ${brief(out.stdout)}` : 'Code ran successfully; no text output was returned.';
    } else if (name === 'request_tools' && Array.isArray(out.activated)) {
        step.result = `Activated ${out.activated.length} capabilities for the next steps.`;
        if (Array.isArray(out.not_found) && out.not_found.length) {
            step.status = 'Partial';
            caveats.push('Some requested capabilities were unavailable.');
        }
    } else if (name === 'discover_tools' && Array.isArray(out.results || out.tools)) {
        const tools = out.results || out.tools;
        step.result = `Found ${tools.length} available capabilities.`;
        if (!tools.length) step.status = 'No matches';
    } else if (config?.isValidOutput(part.output)) {
        // Config formatters only see schemas they recognize; stale configs cannot turn
        // a valid new payload into a spurious error or a fabricated zero-result search.
        if (config.isErrorResult?.(part.output)) {
            step.status = 'Failed';
            step.result = config.formatError(part.output);
        } else {
            if (config.isEmptyResult?.(part.output)) step.status = 'No matches';
            step.result = config.formatSuccess(part.output);
        }
    }
    if (step.status === 'Partial' && !caveats.length) caveats.push('Only part of the available result was returned.');
    return step;
}
